import {
  LiveDraftTransitionStore,
  useLiveDraftTransitionStore,
} from "@/data-preparation-tool/live-draft-transition-store";
import { Projection } from "@/data-preparation-tool/ui/projection-switch";
import { applySavedView } from "@/features/saved-views/apply-saved-view";
import { useSavedView } from "@/features/saved-views/saved-view-context";
import {
  SceneConversionState,
  useSceneConversionState,
} from "@/hooks/use-scene-conversion-state";
import { isTopDownView } from "@/registration-tools/utils/camera-views";
import { selectAllCadModels } from "@/store/cad/cad-selectors";
import {
  changeMode,
  ModeInitialState,
  selectIsSavedViewApplied,
} from "@/store/mode-slice";
import {
  selectActiveArea,
  selectActiveElement,
  selectIsProjectEmpty,
} from "@/store/selections-selectors";
import { AppThunk, RootState } from "@/store/store";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  selectCanReadCAD,
  selectCanReadPointCloud,
} from "@/store/subscription-features/subscription-features-selectors";
import { setShowSpinner } from "@/store/ui/ui-slice";
import { SceneFilter } from "@/types/scene-filter";
import { useEffect } from "react";
import { getMode } from ".";
import { Mode, ModeNames } from "./mode";
import { selectCurrentAreaData } from "./select-current-area-data";

/**
 * Mode to handle the start of the application
 */
export const startMode: Mode = {
  name: "start",

  ModeScene() {
    const dispatch = useAppDispatch();

    const isProjectEmpty = useAppSelector(selectIsProjectEmpty);
    const savedView = useSavedView();
    const liveDraftTransitionState = useLiveDraftTransitionStore();

    const sceneConversionState = useSceneConversionState();

    const { getState } = useAppStore();

    // When app starts
    useEffect(() => {
      if (!sceneConversionState) {
        dispatch(setShowSpinner(true));
        return;
      }

      dispatch(setShowSpinner(false));
      if (sceneConversionState !== SceneConversionState.Ok) {
        dispatch(changeMode("projectConversion"));
      } else if (isProjectEmpty) {
        dispatch(changeMode("createArea"));
      } else if (
        savedView &&
        !(savedView instanceof Error) &&
        // Each view is applied once. Applying the same one again on a later
        // return to the start mode would discard the state the user set since,
        // while a view that has not been applied yet is a new one to open. The
        // thunk records the view it applied, which is what this reads.
        !selectIsSavedViewApplied(savedView)(getState())
      ) {
        dispatch(applySavedView(savedView));
      } else if (liveDraftTransitionState.camera) {
        dispatch(prepareAppStateFromDraftView(liveDraftTransitionState));
      } else {
        dispatch(changeMode("sheet"));
      }
    }, [
      savedView,
      liveDraftTransitionState,
      dispatch,
      isProjectEmpty,
      getState,
      sceneConversionState,
    ]);

    return null;
  },
  ModeOverlay() {
    // TODO: Better loading screen
    return <p>Loading.....</p>;
  },
};

/**
 * Initialize the state of the Live view based on the previously used state in the Draft view for a seamless transition.
 *
 * @param liveDraftTransitionState the data used for the transition between Live and Draft view.
 * @returns a thunk to set the initial state based on the data from the Draft view.
 */
function prepareAppStateFromDraftView({
  camera,
  projection,
  orbitControls,
}: LiveDraftTransitionStore): AppThunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const modeName = selectModeToRestoreFromDraftView(
      projection,
      camera,
    )(state);

    if (!modeName) {
      // Restoring the Draft camera here may frame something the sheet does not show
      dispatch(changeMode("sheet"));
      return;
    }

    const initialModeState: ModeInitialState =
      getMode(modeName).initialState?.compute(state, {
        cameras: camera ? [camera] : [],
        controls: orbitControls ? [orbitControls] : [],
      }) ?? {};

    // When coming back from the Draft view, the pano scene filter can't be used.
    if (
      modeName === "overview" &&
      initialModeState.scene === SceneFilter.Pano
    ) {
      initialModeState.scene = SceneFilter.PointCloud;
    }

    dispatch(
      changeMode({
        mode: modeName,
        initialState: initialModeState,
      }),
    );
  };
}

/**
 * @param projection the projection used in the Draft view.
 * @param camera the camera used in the Draft view.
 * @returns a selector for the Live view mode the Draft camera can be restored into, or undefined
 * when no mode matches it, e.g. a 3D camera in a project the 3D overview has no data for.
 */
function selectModeToRestoreFromDraftView(
  projection: LiveDraftTransitionStore["projection"],
  camera: LiveDraftTransitionStore["camera"],
) {
  return (state: RootState): ModeNames | undefined => {
    // The sheet mode only supports a top-down view
    if (projection === Projection.twoDimensional && isTopDownView(camera)) {
      return "sheet";
    }
    return selectHasOverviewData(state) ? "overview" : undefined;
  };
}

/**
 * Mirrors the availability check of the 3D overview button in useOverviewToggleState of ModeSwitch.
 *
 * @param state the application state.
 * @returns whether the current area has data the 3D overview can render.
 */
function selectHasOverviewData(state: RootState): boolean {
  const element = selectActiveElement(state) ?? selectActiveArea(state);
  const areaData = selectCurrentAreaData(element)(state);
  if (!areaData) {
    return false;
  }
  const { dataSessions3d, dataSessions5d } = areaData;

  const hasGaussianSplats = !!dataSessions3d.gaussianSplats.length;
  if (hasGaussianSplats) {
    return true;
  }
  const hasPointCloud =
    (!!dataSessions3d.pointClouds.length || !!dataSessions5d.length) &&
    selectCanReadPointCloud(state);
  if (hasPointCloud) {
    return true;
  }

  const hasCad = !!selectAllCadModels(state).length && selectCanReadCAD(state);
  return hasCad;
}
